import { useState, useEffect, useRef } from "react";
import API from "../lib/api";

function ScoreBar({ label, value, accent }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: accent }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "rgba(0,0,0,0.08)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${accent}cc)`, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

export default function InterviewChat({ jobId, isDark, t = {}, onComplete, fullHeight = false }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [thinking, setThinking] = useState(false);
  const [report, setReport] = useState(null);
  const [initError, setInitError] = useState(null);
  const bottomRef = useRef(null);

  const textPrimary = t.textPrimary || (isDark ? "#ffffff" : "#0a0a0f");
  const textSecondary = t.textSecondary || (isDark ? "rgba(255,255,255,0.5)" : "#64748b");
  const textMuted = t.textMuted || (isDark ? "rgba(255,255,255,0.3)" : "#94a3b8");
  const accent = t.accent || "#f97316";
  const accentDark = t.accentDark || "#ea6a08";

  const bubbleBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)";
  const inputBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)";
  const inputBorder = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";
  const aiBubbleBg = isDark ? "rgba(26,31,94,0.8)" : "rgba(26,31,94,0.07)";
  const aiBubbleColor = isDark ? "#93c5fd" : "#1a1f5e";
  const userBubbleBg = `linear-gradient(135deg, ${accent}, ${accentDark})`;

  // Start session on mount
  useEffect(() => {
    setThinking(true);
    API.post("/api/interviews/schedule", { job_id: jobId || null })
      .then(r => {
        setSessionId(r.data.id || r.data.session_id);
        const opening = r.data.opening_message || r.data.message;
        if (opening) {
          setMessages([{ role: "ai", text: opening }]);
        }
      })
      .catch(err => {
        setInitError(err?.response?.data?.detail || err?.message || "Failed to start interview session");
      })
      .finally(() => setThinking(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  async function sendMessage() {
    const text = inputText.trim();
    if (!text || !sessionId || thinking) return;
    setInputText("");
    setMessages(prev => [...prev, { role: "user", text }]);
    setThinking(true);
    try {
      const res = await API.post(`/api/interviews/${sessionId}/message`, { content: text });
      const data = res.data;
      if (data.reply || data.message) {
        setMessages(prev => [...prev, { role: "ai", text: data.reply || data.message }]);
      }
      if (data.complete) {
        setReport(data.report || data);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setThinking(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const overallScore = report?.overall_score ?? report?.score ?? null;
  const scoreColor = overallScore >= 75 ? "#059669" : overallScore >= 50 ? "#f97316" : "#dc2626";

  if (initError) {
    return (
      <div data-testid="interview-chat" style={{ padding: 16, textAlign: "center", color: "#dc2626", fontSize: 13 }}>
        {initError}
      </div>
    );
  }

  if (report) {
    return (
      <div data-testid="interview-report" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Score badge */}
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "16px 32px", borderRadius: 16, background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", border: `1px solid ${bubbleBorder}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: textMuted }}>Overall Score</span>
            <span style={{ fontSize: 48, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{overallScore ?? "--"}</span>
            {overallScore !== null && <span style={{ fontSize: 12, color: textSecondary }}>/100</span>}
          </div>
        </div>

        {/* Score bars */}
        <div style={{ padding: "12px 16px", borderRadius: 10, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)", border: `1px solid ${bubbleBorder}` }}>
          <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: textMuted, marginBottom: 12, marginTop: 0 }}>Score Breakdown</p>
          <ScoreBar label="Communication Clarity" value={report.communication_clarity} accent={accent} />
          <ScoreBar label="STAR Format" value={report.star_format} accent={accent} />
          <ScoreBar label="Role Relevance" value={report.role_relevance} accent={accent} />
        </div>

        {/* Strengths */}
        {Array.isArray(report.strengths) && report.strengths.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: textMuted, marginBottom: 8, marginTop: 0 }}>Strengths</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {report.strengths.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: textSecondary }}>
                  <span style={{ color: "#059669", flexShrink: 0 }}>✓</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Improvements */}
        {Array.isArray(report.improvements) && report.improvements.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: textMuted, marginBottom: 8, marginTop: 0 }}>Areas to Improve</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {report.improvements.map((imp, i) => (
                <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: textSecondary }}>
                  <span style={{ color: accent, flexShrink: 0 }}>→</span>
                  <span>{imp}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {report.summary && (
          <p style={{ fontSize: 13, lineHeight: 1.6, color: textSecondary, padding: "10px 14px", borderRadius: 8, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${bubbleBorder}`, margin: 0 }}>
            {report.summary}
          </p>
        )}

        {/* Done button */}
        <button
          onClick={onComplete}
          style={{ alignSelf: "center", padding: "10px 32px", borderRadius: 8, border: "none", cursor: "pointer", background: `linear-gradient(135deg, #1a1f5e, #2d3494)`, color: "white", fontSize: 14, fontWeight: 600, boxShadow: "0 2px 8px rgba(26,31,94,0.3)" }}
          data-testid="interview-done-btn"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div data-testid="interview-chat" style={{ display: "flex", flexDirection: "column", ...(fullHeight ? { flex: 1, minHeight: 0, height: "100%" } : { height: 480 }) }}>
      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, padding: "4px 0 12px" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "80%", padding: "9px 14px", borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: msg.role === "user" ? userBubbleBg : aiBubbleBg, color: msg.role === "user" ? "#ffffff" : aiBubbleColor, fontSize: 13, lineHeight: 1.5, wordBreak: "break-word" }}>
              {msg.text}
            </div>
          </div>
        ))}

        {thinking && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "9px 14px", borderRadius: "16px 16px 16px 4px", background: aiBubbleBg, color: textMuted, fontSize: 12, fontStyle: "italic" }}>
              AI is thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div style={{ display: "flex", gap: 8, paddingTop: 12, borderTop: `1px solid ${bubbleBorder}` }}>
        <textarea
          data-testid="chat-input"
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={thinking || !sessionId}
          rows={2}
          placeholder="Type your answer... (Enter to send)"
          style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: `1px solid ${inputBorder}`, background: inputBg, color: textPrimary, fontSize: 13, resize: "none", outline: "none", fontFamily: "inherit", lineHeight: 1.5 }}
        />
        <button
          data-testid="send-message-btn"
          onClick={sendMessage}
          disabled={thinking || !inputText.trim() || !sessionId}
          style={{ padding: "0 18px", borderRadius: 10, border: "none", cursor: thinking || !inputText.trim() || !sessionId ? "not-allowed" : "pointer", background: thinking ? "rgba(249,115,22,0.4)" : `linear-gradient(135deg, ${accent}, ${accentDark})`, color: "white", fontSize: 13, fontWeight: 600, flexShrink: 0, boxShadow: thinking ? "none" : "0 2px 8px rgba(249,115,22,0.3)" }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
